import classNames from 'classnames';

import { CryptocurrenciesEnum, PaymentMethodEnum } from '../../../lib/enums';
import { PaymentStatusEnum } from '../../../lib/enums/payments/payment-status.enum';
import lang from '../../../lib/language';
import { Payment } from '../../../lib/models/crm/types';
import { isWithdrawal } from '../../../lib/store/selectors';
import { formatTransaction } from '../../../lib/util/DataHelpers';
import { parseDateTo, ParseToType } from '../../../lib/util/DateTimeHelpers';
import { TextHelpers } from '../../../lib/util/TextHelpers';

import IconMinus from '../../../assets/img/wallet/icon-minus.svg';
import IconPlus from '../../../assets/img/wallet/icon-plus.svg';
import { useIsMobile } from '../../../hooks';

import styles from './Transactions.module.scss';

type Props = {
  payment: Payment
}

const TransactionItem = ({ payment }: Props) => {
  const { isMobile } = useIsMobile();
  const isSolana = payment?.payment_method === PaymentMethodEnum.Solana;
  const displayStatus = () => {
    if (payment.payment_status === PaymentStatusEnum.Completed) {
      return (
        <p className={styles.hidePendingText}>
          {TextHelpers.getPaymentStatusName(payment.payment_status)}
        </p>
      );
    }
    if (payment.payment_status === PaymentStatusEnum.Rejected
      || payment.payment_status === PaymentStatusEnum.Canceled) {
      return (
        <p className={styles.errorText}>
          {TextHelpers.getPaymentStatusName(payment.payment_status)}
        </p>
      );
    }
    if (payment.payment_status === PaymentStatusEnum.ForApproval
       || payment.payment_status === PaymentStatusEnum.Pending
       || payment.payment_status === PaymentStatusEnum.OnHold) {
      return (
        <p className={styles.pendingText}>
          {lang.commonOrderStatusPending()}
        </p>
      );
    }
    return null;
  };

  const displayCryptoAmount = () => {
    if (!isSolana) return null;
    const usdAmount = payment?.amount;
    const rate = payment?.crypto_detail_rate;
    if (!usdAmount || !rate) return null;
    const cryptoAmount = rate * usdAmount;
    return (
      <div className={styles.cryptoAmountLabel}>{`${CryptocurrenciesEnum.USDT} ${cryptoAmount.toFixed(4)}`}</div>
    );
  };

  return (
    <>
      {isMobile ? (
        <>
          <div className={styles.wrapperInfoMobile}>
            <div className={styles.iconWrapper}>
              <img
                className={styles.icon}
                src={
                  (payment.amount < 0 || isWithdrawal(payment))
                    ? IconMinus
                    : IconPlus
                }
                alt="icon arrow"
              />
            </div>
            <div className={styles.infoWrapper}>
              <div className={styles.wrapperLine}>
                <div className={styles.paymentType}>
                  {TextHelpers.getPaymentProcessName(payment.payment_process)}
                  {isSolana && <div className={styles.cryptoLabel}>{` • ${lang.payoutsCrypto()}`}</div>}
                </div>
                <div className={classNames(styles.paymentAmount,
                  {
                    [styles.paymentAmountRejected]: payment.payment_status
                      === PaymentStatusEnum.Rejected,
                  })}
                >
                  {formatTransaction(payment, 'amount')}
                  {displayCryptoAmount()}
                </div>
              </div>
              <div className={styles.wrapperLine}>
                <div className={styles.paymentStatus}>
                  {displayStatus()}
                </div>
                <div className={styles.paymentDate}>
                  {`${parseDateTo(new Date(payment.create_date), ParseToType.displayFormat)}`}
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <div className={styles.wrapperTransaction}>
          <div className={styles.transactionDetails}>
            <div className={styles.iconWrapper}>
              <img
                className={styles.icon}
                src={
                  (payment.amount < 0 || isWithdrawal(payment))
                    ? IconMinus
                    : IconPlus
                }
                alt="icon arrow"
              />
            </div>
            <div className={styles.paymentInfoWrapper}>
              <div className={styles.paymentType}>
                <div>{TextHelpers.getPaymentProcessName(payment.payment_process)}</div>
                {isSolana && <div className={styles.cryptoLabel}>{` • ${lang.payoutsCrypto()}`}</div>}
              </div>
              <div className={styles.paymentInfo}>
                <div className={styles.paymentDate}>
                  {`${parseDateTo(new Date(payment.create_date), ParseToType.displayFormat)}`}
                </div>
                {displayStatus()}
                {[
                  PaymentStatusEnum.Canceled,
                  PaymentStatusEnum.ForApproval,
                  PaymentStatusEnum.Rejected,
                  PaymentStatusEnum.ForApproval,
                ].includes(payment.payment_status)}
              </div>
            </div>
          </div>
          <div className={classNames(styles.paymentAmount,
            {
              [styles.paymentAmountRejected]: payment.payment_status
                === PaymentStatusEnum.Rejected,
            })}
          >
            {formatTransaction(payment, 'amount')}
            {displayCryptoAmount()}
          </div>
        </div>
      )}
    </>
  );
};

export default TransactionItem;
